/**
 * Side-effect module: must be imported from index.ts BEFORE `./App` so a
 * chunk-load failure caused by a new deploy (a session holding an old HTML/
 * bundle asks for hashed chunks that no longer exist) triggers one automatic
 * reload instead of leaving the UI dead mid-transaction.
 *
 * Reloads are throttled via sessionStorage so a persistent failure cannot
 * loop forever.
 */
import { Platform } from 'react-native';

const RELOAD_FLAG = 'vuelto.chunkReloadGuard.lastReloadAt';
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  'loading chunk', // webpack ChunkLoadError
  'loading module',
  'dynamically imported module', // "Failed to fetch dynamically imported module"
  'chunkloaderror',
  'importing a module script failed', // Safari
];

/** True si el mensaje es un fallo de lazy chunk/módulo (deploy asset mismatch). */
export function isChunkLoadErrorMessage(message: string): boolean {
  const m = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => m.includes(p));
}

function reloadOnce(): void {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage bloqueado: recargar igual */
  }
  window.location.reload();
}

function isAppScriptElement(target: unknown): target is HTMLScriptElement {
  return (
    typeof target === 'object' &&
    target !== null &&
    (target as { tagName?: string }).tagName === 'SCRIPT' &&
    typeof (target as { src?: string }).src === 'string' &&
    (target as { src: string }).src.includes('/_expo/static/js/')
  );
}

export function installWebChunkReloadGuard(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;

  // capture: los errores de recursos (<script src>) no burbujean.
  window.addEventListener(
    'error',
    (event) => {
      const message = event.message ?? String(event.error ?? '');
      if (isChunkLoadErrorMessage(message) || isAppScriptElement(event.target)) {
        reloadOnce();
      }
    },
    true,
  );

  // import() rechazado → unhandledrejection (no dispara 'error').
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      typeof reason === 'string' ? reason : ((reason as { message?: string })?.message ?? String(reason ?? ''));
    if (isChunkLoadErrorMessage(message)) {
      event.preventDefault();
      reloadOnce();
    }
  });
}

installWebChunkReloadGuard();
